import * as L from 'leaflet';
import { makeCoords } from '../types/map-types';

export interface TileRule {
	zoomRange: [number, number];
	source: 'assets' | 'wms' | 'tiles';
	pathOrUrl: string;
	bbox?: L.LatLngBounds;
	fallbackTo?: TileRule;
	maxNativeZoom?: number;
	subdomains?: string[];
}

export interface HybridOsmLayerOptions extends L.TileLayerOptions {
	rules: TileRule[];
	wmsCrossOrigin?: boolean;
	maxFallbackDepth?: number;
}

interface ResolvedTile {
	url: string;
	srcCoords: L.Coords;
}

const tileImageCache = new Map<string, HTMLImageElement>();

export class HybridOsmLayer extends L.TileLayer {
	declare options: HybridOsmLayerOptions;

	constructor(options: HybridOsmLayerOptions) {
		const opts = {
			...options,
			updateWhenZooming: false,
			updateWhenIdle: true,
			keepBuffer: 2
		};
		super('', opts);
	}

	private getCacheKey(url: string): string {
		return url;
	}

	private getCachedImage(url: string): HTMLImageElement | null {
		return tileImageCache.get(this.getCacheKey(url)) || null;
	}

	private cacheImage(url: string, img: HTMLImageElement): void {
		if (tileImageCache.size > 500) {
			const firstKey = tileImageCache.keys().next().value;
			if (firstKey) tileImageCache.delete(firstKey);
		}
		tileImageCache.set(this.getCacheKey(url), img);
	}

	override getTileUrl(coords: L.Coords): string {
		const resolved = this.resolveTileSourceWithDebug(coords, this.options.rules, 0, []);
		return resolved ? resolved.url : '';
	}

	override createTile(coords: L.Coords, done: L.DoneCallback): HTMLElement {
		const tileSize = (this.options.tileSize as number) || 256;
		const triedRules: string[] = [];
		const resolved = this.resolveTileSourceWithDebug(coords, this.options.rules, 0, triedRules);

		if (!resolved) {
			const canvas = document.createElement('canvas');
			canvas.width = tileSize;
			canvas.height = tileSize;
			setTimeout(() => done(undefined, canvas), 0);
			return canvas;
		}

		const { url, srcCoords } = resolved;
		const isOnline = url.startsWith('http');
		const cachedImg = isOnline ? this.getCachedImage(url) : null;

		if (srcCoords.z === coords.z) {
			const img = document.createElement('img') as HTMLImageElement;
			img.alt = '';

			if (cachedImg) {
				img.src = cachedImg.src;
				setTimeout(() => done(undefined, img), 0);
			} else {
				if (isOnline) img.crossOrigin = 'anonymous';
				img.onload = () => {
					if (isOnline) this.cacheImage(url, img);
					done(undefined, img);
				};
				img.onerror = () => {
					if (!isOnline) {
						const onlineUrl = this.getOnlineFallbackUrl(coords);
						if (onlineUrl) {
							img.onerror = () => done(undefined, img);
							img.src = onlineUrl;
							return;
						}
					}
					done(undefined, img);
				};
				img.src = url;
			}
			return img;
		}

		const canvas = document.createElement('canvas');
		canvas.width = tileSize;
		canvas.height = tileSize;
		const ctx = canvas.getContext('2d')!;
		ctx.imageSmoothingEnabled = false;

		const drawScaled = (sourceImg: HTMLImageElement) => {
			const dz = coords.z - srcCoords.z;
			const scale = Math.pow(2, dz);

			const subX = coords.x % scale;
			const subY = coords.y % scale;

			const srcTileSize = tileSize;
			const srcSubSize = srcTileSize / scale;

			const sx = subX * srcSubSize;
			const sy = subY * srcSubSize;
			ctx.drawImage(sourceImg, sx, sy, srcSubSize, srcSubSize, 0, 0, tileSize, tileSize);
			done(undefined, canvas);
		};

		if (cachedImg) {
			setTimeout(() => drawScaled(cachedImg), 0);
		} else {
			const img = new Image();
			if (isOnline) img.crossOrigin = 'anonymous';

			img.onload = () => {
				if (isOnline) this.cacheImage(url, img);
				drawScaled(img);
			};

			img.onerror = () => {
				ctx.clearRect(0, 0, tileSize, tileSize);
				done(undefined, canvas);
			};

			img.src = url;
		}
		return canvas;
	}

	private resolveTileSourceWithDebug(coords: L.Coords, rules: TileRule[], depth: number, triedRules: string[]): ResolvedTile | null {
		const maxDepth = this.options.maxFallbackDepth ?? 8;
		if (depth > maxDepth) return null;

		const tileBounds = this.tileCoordsToLatLngBounds(coords);

		for (const rule of rules) {
			triedRules.push(`${rule.zoomRange[0]}→${rule.zoomRange[1]}`);
			const [minZ, maxZ] = rule.zoomRange;
			if (coords.z < minZ || coords.z > maxZ) continue;
			if (!rule.bbox || rule.bbox.intersects(tileBounds)) {
				const effectiveZoom = rule.maxNativeZoom && coords.z > rule.maxNativeZoom
					? rule.maxNativeZoom
					: coords.z;

				if (effectiveZoom !== coords.z) {
					const scale = Math.pow(2, coords.z - effectiveZoom);
					const lowerCoords = makeCoords(
						Math.floor(coords.x / scale),
						Math.floor(coords.y / scale),
						effectiveZoom
					);
					const url = this.buildUrlForRule(rule, lowerCoords);
					return { url, srcCoords: lowerCoords };
				}

				const url = this.buildUrlForRule(rule, coords);
				return { url, srcCoords: coords };
			}
		}

		for (const rule of rules) {
			if (!rule.fallbackTo) continue;
			const fbRule = rule.fallbackTo;
			let fallbackZoom = fbRule.zoomRange[1];
			if (fallbackZoom >= coords.z) fallbackZoom = Math.max(fbRule.zoomRange[0], coords.z - 1);
			if (fallbackZoom >= coords.z) continue;

			const scale = Math.pow(2, coords.z - fallbackZoom);
			const lowerCoords: L.Coords = makeCoords(
				Math.floor(coords.x / scale),
				Math.floor(coords.y / scale),
				fallbackZoom
			);

			const resolved = this.resolveTileSourceWithDebug(lowerCoords, [fbRule], depth + 1, triedRules);
			if (resolved) return resolved;
		}

		return null;
	}

	private buildUrlForRule(rule: TileRule, coords: L.Coords): string {
		switch (rule.source) {
			case 'assets':
				return `${rule.pathOrUrl}/${coords.z}/${coords.x}/${coords.y}.png`;
			case 'tiles':
				return this.buildTileUrl(coords, rule.pathOrUrl, rule.subdomains);
			case 'wms':
				return this.buildWmsUrl(coords, rule.pathOrUrl);
		}
	}

	private getOnlineFallbackUrl(coords: L.Coords): string | null {
		for (const rule of this.options.rules) {
			if (rule.source === 'tiles') {
				return this.buildTileUrl(coords, rule.pathOrUrl, rule.subdomains);
			}
		}
		return `https://tile.openstreetmap.org/${coords.z}/${coords.x}/${coords.y}.png`;
	}

	private buildTileUrl(coords: L.Coords, urlTemplate: string, subdomains?: string[]): string {
		let url = urlTemplate
			.replace('{z}', String(coords.z))
			.replace('{x}', String(coords.x))
			.replace('{y}', String(coords.y));

		if (subdomains && subdomains.length > 0 && url.includes('{s}')) {
			const index = (coords.x + coords.y) % subdomains.length;
			url = url.replace('{s}', subdomains[index]);
		}

		return url;
	}

	private buildWmsUrl(coords: L.Coords, baseUrl: string): string {
		const tileBounds = this.tileCoordsToLatLngBounds(coords);
		const mapCRS = (this._map?.options.crs as any) || L.CRS.EPSG3857;
		const sw = mapCRS.project(tileBounds.getSouthWest());
		const ne = mapCRS.project(tileBounds.getNorthEast());
		const bbox = `${sw.x},${sw.y},${ne.x},${ne.y}`;
		const tileSize = (this.options.tileSize as number) || 256;

		const params = new URLSearchParams({
			SERVICE: 'WMS',
			REQUEST: 'GetMap',
			VERSION: '1.1.1',
			SRS: 'EPSG:3857',
			BBOX: bbox,
			WIDTH: String(tileSize),
			HEIGHT: String(tileSize),
			LAYERS: 'OSM-WMS',
			STYLES: '',
			FORMAT: 'image/png',
			TRANSPARENT: 'TRUE',
		});

		return baseUrl.includes('?') ? baseUrl + params.toString() : baseUrl + '?' + params.toString();
	}

	private tileCoordsToLatLngBounds(coords: L.Coords): L.LatLngBounds {
		const tileSize = (this.options.tileSize as number) || 256;
		const nwPoint = L.point(coords.x * tileSize, coords.y * tileSize);
		const sePoint = nwPoint.add(L.point(tileSize, tileSize));

		const mapCRS = (this._map?.options.crs as L.CRS) || L.CRS.EPSG3857;
		const nwLatLng = mapCRS.pointToLatLng(nwPoint, coords.z);
		const seLatLng = mapCRS.pointToLatLng(sePoint, coords.z);

		return new L.LatLngBounds(nwLatLng, seLatLng);
	}
}
